/**
 * Gestionnaire pour les parsers BBCode avec lazy loading
 * Les parsers sont chargés à la demande via routedFetch() et mis en cache automatiquement
 * Basé sur le code legacy SetBBCodeParser()
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { routedFetch } from '../utils/api-router.js';
import { createSpecializedHelper } from './regexpHelper.js';

// Helpers spécialisés pour BBCode - chargés en lazy loading
let bbcodeToHtmlHelper = null;
let htmlToBBCodeHelper = null;
let canonTextHelper = null;
let bbcodeBeautifyClosingRegexp = null;
let bbcodeBeautifyOpeningRegexp = null;

const bbcodeBeautifyClosingPattern = '(\\[/(p|h1|h2|h3|h4|h5|h6|aloud|quote|list|table)\\])\\s*';
const bbcodeBeautifyOpeningPattern = '(\\[(p|h1|h2|h3|h4|h5|h6|aloud|quote|list|table)\\])\\s+';

/*
** BBCode Beautifier
** Supprime les sauts de ligne et espaces blancs après certains tags BBCode de type bloc
** Exemple : [h1]Titre[/h1]\n \n devient [h1]Titre[/h1]
** Exemple : [p]\n \nTexte devient [p]Texte
*/
export const beautifyBBCode = (text) => {
  // Vérifier que text est une string valide
  if (typeof text !== 'string') {
    return String(text || '');
  }
  
  // Initialiser les regexp de beautification si nécessaire
  if (!bbcodeBeautifyClosingRegexp) {
    bbcodeBeautifyClosingRegexp = new RegExp(bbcodeBeautifyClosingPattern, 'gis');
  }
  if (!bbcodeBeautifyOpeningRegexp) {
    bbcodeBeautifyOpeningRegexp = new RegExp(bbcodeBeautifyOpeningPattern, 'gis');
  }
  
  // Supprimer les sauts de ligne après les balises fermantes
  text = text.replace(bbcodeBeautifyClosingRegexp, '$1\n');
  
  // Supprimer les sauts de ligne après les balises ouvrantes
  text = text.replace(bbcodeBeautifyOpeningRegexp, '$1');
  
  return text;
};

/**
 * Fonction privée : Charge les règles BBCode->HTML via lazy loading
 * Auto-cachée pour 6h par routedFetch()
 */
const getBBCodeToHtmlHelper = async () => {
  if (!bbcodeToHtmlHelper) {
    bbcodeToHtmlHelper = createSpecializedHelper('BBCodeToHtml', async (helper) => {
      debug.log(config, 'BBCodeHelper: Chargement des règles BBCode->HTML depuis l\'API');
      helper.clearRules();
      
      const data = await routedFetch('/api/config/read/regexp-rules?usage_type=BBCODE2HTML');
      
      if (!data || !data['regexp-rules']) {
        throw new Error('Impossible de récupérer les règles BBCode depuis l\'API');
      }
      
      const apiRules = data['regexp-rules'];
      debug.log(config, 'BBCodeHelper: Règles API récupérées:', apiRules.length);
      
      // Charger les règles depuis l'API
      helper.loadRulesFromArray(apiRules, 'API:regexpRules', { usageType: 'BBCODE2HTML' });
      
      // Charger les formats personnalisés depuis le localStorage
      helper.loadRulesFromLocalStorage('bel_formats', (belFormats) => {
        const rules = [];
        if (belFormats?.customFormats) {
          for (let i = 0; i < belFormats.customFormats.length; i++) {
            const format = belFormats.customFormats[i];
            if (format?.field2?.trim()) {
              rules.push({
                searchPattern: format.field2,
                replacePattern: `<p class="customformat${i}">$1</p>`,
                metadata: {
                  formatIndex: i,
                  formatType: 'customFormat'
                }
              });
            }
          }
        }
        return rules;
      });
      
      debug.log(config, 'BBCodeHelper: Règles BBCode->HTML chargées:', helper.getRulesCount());
    });
    
    await bbcodeToHtmlHelper.initialize();
  }
  return bbcodeToHtmlHelper;
};

/**
 * Fonction privée : Charge les règles HTML->BBCode via lazy loading
 * Auto-cachée pour 6h par routedFetch()
 */
const getHtmlToBBCodeHelper = async () => {
  if (!htmlToBBCodeHelper) {
    htmlToBBCodeHelper = createSpecializedHelper('HtmlToBBCode', async (helper) => {
      debug.log(config, 'BBCodeHelper: Chargement des règles HTML->BBCode depuis l\'API');
      helper.clearRules();
      
      const data = await routedFetch('/api/config/read/regexp-rules?usage_type=HTML2BBCODE');
      
      if (!data || !data['regexp-rules']) {
        throw new Error('Impossible de récupérer les règles HTML->BBCode depuis l\'API');
      }
      
      const apiRules = data['regexp-rules'];
      debug.log(config, 'BBCodeHelper: Règles HTML->BBCode API récupérées:', apiRules.length);
      
      helper.loadRulesFromArray(apiRules, 'API:regexpRules', { usageType: 'HTML2BBCODE' });
      
      // Charger les formats personnalisés depuis localStorage
      helper.loadRulesFromLocalStorage('bel_formats', (belFormats) => {
        const rules = [];
        if (belFormats?.customFormats) {
          for (let i = 0; i < belFormats.customFormats.length; i++) {
            const format = belFormats.customFormats[i];
            if (format?.field1) {
              rules.push({
                searchPattern: `<p class="customformat${i+1}">(.+?)</p>`,
                replacePattern: format.field1,
                metadata: {
                  formatIndex: i,
                  formatType: 'customFormat'
                }
              });
            }
          }
        }
        
        if (belFormats?.bbcodeImageFormat) {
          rules.push({
            searchPattern: '(\\[img:[0-9]+\\])',
            replacePattern: belFormats.bbcodeImageFormat,
            metadata: {
              formatType: 'bbcodeImageFormat'
            }
          });
        }
        
        return rules;
      });
      
      // Charger les raccourcis depuis le localStorage
      helper.loadRulesFromLocalStorage('bel_shortcuts', (belShortcuts) => {
        const rules = [];
        if (belShortcuts?.shortcuts) {
          for (const shortcut of belShortcuts.shortcuts) {
            if (shortcut.field1 && shortcut.field2) {
              const pattern = '@' + shortcut.field1 + ' ?([^@]+)@';
              rules.push({
                searchPattern: pattern,
                replacePattern: shortcut.field2,
                metadata: {
                  shortcutKey: shortcut.field1,
                  formatType: 'shortcut'
                }
              });
            }
          }
        }
        return rules;
      });
      
      debug.log(config, 'BBCodeHelper: Règles HTML->BBCode chargées:', helper.getRulesCount());
    });
    
    await htmlToBBCodeHelper.initialize();
    // Initialiser aussi les beautifiers
    bbcodeBeautifyClosingRegexp = new RegExp(bbcodeBeautifyClosingPattern, 'gis');
    bbcodeBeautifyOpeningRegexp = new RegExp(bbcodeBeautifyOpeningPattern, 'gis');
  }
  return htmlToBBCodeHelper;
};

/**
 * Fonction privée : Charge les règles CANONTEXT via lazy loading
 * Auto-cachée pour 6h par routedFetch()
 */
const getCanonTextHelper = async () => {
  if (!canonTextHelper) {
    canonTextHelper = createSpecializedHelper('CanonText', async (helper) => {
      debug.log(config, 'BBCodeHelper: Chargement des règles CANONTEXT depuis l\'API');
      helper.clearRules();
      
      const data = await routedFetch('/api/config/read/regexp-rules?usage_type=CANONTEXT');
      
      if (!data || !data['regexp-rules']) {
        throw new Error('Impossible de récupérer les règles CANONTEXT depuis l\'API');
      }
      
      const apiRules = data['regexp-rules'];
      debug.log(config, 'BBCodeHelper: Règles CANONTEXT API récupérées:', apiRules.length);
      
      helper.loadRulesFromArray(apiRules, 'API:regexpRules', { usageType: 'CANONTEXT' });
      
      debug.log(config, 'BBCodeHelper: Règles CANONTEXT chargées:', helper.getRulesCount());
    });
    
    await canonTextHelper.initialize();
  }
  return canonTextHelper;
};

/**
 * Applique les règles BBCode->HTML sur un texte
 * Lazy-load automatique des règles à la première utilisation
 * @param {string} text - Texte à traiter
 * @returns {Promise<string>} - Texte traité
 */
export const convertBBCodeToHtml = async (text) => {
  try {
    const helper = await getBBCodeToHtmlHelper();
    return helper.applyConversionRules(text, 'BBCode->HTML');
  } catch (error) {
    debug.error(config, 'BBCodeHelper: Erreur conversion BBCode->HTML:', error);
    return text;
  }
};

/**
 * Applique les règles HTML->BBCode sur un texte
 * Lazy-load automatique des règles à la première utilisation
 * @param {string} text - Texte à traiter
 * @returns {Promise<string>} - Texte traité
 */
export const convertHtmlToBBCode = async (text) => {
  try {
    const helper = await getHtmlToBBCodeHelper();
    return helper.applyConversionRules(text, 'HTML->BBCode');
  } catch (error) {
    debug.error(config, 'BBCodeHelper: Erreur conversion HTML->BBCode:', error);
    return text;
  }
};

/**
 * Applique les règles de canonisation sur un texte
 * Lazy-load automatique des règles à la première utilisation
 * @param {string} text - Texte à traiter
 * @returns {Promise<string>} - Texte traité
 */
export const convertCanonText = async (text) => {
  try {
    const helper = await getCanonTextHelper();
    return helper.applyConversionRules(text, 'CANONTEXT');
  } catch (error) {
    debug.error(config, 'BBCodeHelper: Erreur conversion CANONTEXT:', error);
    return text;
  }
};

/**
 * Recharge les règles depuis l'API
 * Utile si les règles ont été modifiées dans l'admin
 */
export const reloadRegexpRules = async () => {
  debug.log(config, 'BBCodeHelper: Rechargement de toutes les règles');
  if (bbcodeToHtmlHelper) {
    await bbcodeToHtmlHelper.reload();
  }
  if (htmlToBBCodeHelper) {
    await htmlToBBCodeHelper.reload();
  }
  if (canonTextHelper) {
    await canonTextHelper.reload();
  }
};

/**
 * Recharge les règles BBCode->HTML depuis l'API
 */
export const reloadBBCodeToHtmlRules = async () => {
  debug.log(config, 'BBCodeHelper: Rechargement des règles BBCode->HTML');
  if (bbcodeToHtmlHelper) {
    await bbcodeToHtmlHelper.reload();
  }
};

/**
 * Recharge les règles HTML->BBCode depuis l'API
 */
export const reloadHtmlToBBCodeRules = async () => {
  debug.log(config, 'BBCodeHelper: Rechargement des règles HTML->BBCode');
  if (htmlToBBCodeHelper) {
    await htmlToBBCodeHelper.reload();
  }
};

/**
 * Recharge les règles CANONTEXT depuis l'API
 */
export const reloadCanonTextRules = async () => {
  debug.log(config, 'BBCodeHelper: Rechargement des règles CANONTEXT');
  if (canonTextHelper) {
    await canonTextHelper.reload();
  }
};

